import { measuredLegendRows } from '@better-lights/core';
import { useAccount } from '../account/store.ts';
import { brandingOf, loadCatalog } from '../branding/catalog.ts';
import { sceneFor } from '../editor/scene.ts';
import { renderProjectPixels } from '../export/offscreen.ts';
import { renderExport } from '../export/render.ts';
import { currentFlags } from '../plans/gate.ts';
import { flushAutosave } from '../state/autosave.ts';
import { useSettings } from '../state/settingsStore.ts';
import { db } from '../storage/db.ts';
import { storage } from '../storage/index.ts';
import { loadQuoteFonts } from './fonts.ts';
import { buildQuoteModel, type QuoteModel, quoteTier } from './model.ts';
import { buildQuotePdf } from './pdf.ts';

const TIME_LABEL = { day: 'Day', evening: 'Evening', night: 'Night' } as const;
const bytesOf = async (b: Blob | null) => (b ? new Uint8Array(await b.arrayBuffer()) : null);

export interface GeneratedQuote {
  bytes: Uint8Array;
  fileName: string;
  model: QuoteModel;
}

/** Workspace of a project: its sync scope, else the active workspace (signed in), else none. */
export async function projectScopeOf(projectId: string): Promise<string | null> {
  return (await db().projectSync.get(projectId))?.scope ?? useAccount.getState().activeScope;
}

/** Builds the quote PDF for a saved project (docs/spec/08 §3). */
export async function generateQuote(
  projectId: string,
  opts: { includeAiRenders?: boolean; date?: Date } = {},
): Promise<GeneratedQuote> {
  await flushAutosave();
  const file = await storage().projects.get(projectId);
  if (!file) throw new Error('The project is missing.');
  const scope = await projectScopeOf(projectId);
  const flags = currentFlags(scope);
  const tier = quoteTier(flags);
  const { me, session } = useAccount.getState();
  let branding = null;
  if (tier !== 'basic' && scope && me) {
    const orgScope = scope !== me.personalScope;
    branding = brandingOf(await loadCatalog(orgScope && flags.company_branding ? scope : me.personalScope));
  }
  const settings = useSettings.getState();
  const scene = sceneFor(
    {
      photo: file.photo,
      runs: file.doc.runs,
      time: file.doc.time,
      style: file.doc.style,
      colorId: file.doc.colorId,
      frameWidthFt: file.doc.frameWidthFt,
    },
    settings.customHex,
  );
  if (!scene) throw new Error('The photo is missing.');
  const rows = measuredLegendRows(scene, file.doc.legendLabels, file.doc.measuredFt);
  const pixels = await renderProjectPixels(file, ['demo', 'lines'], settings.customHex);
  const image = (kind: 'demo' | 'lines') =>
    renderExport(kind, {
      render: (v) => pixels[v],
      scene,
      labels: file.doc.legendLabels,
      measuredFt: file.doc.measuredFt,
      currency: settings.currency,
      units: settings.units,
      legendEnabled: true,
      showLengths: true,
      format: 'jpeg',
      flags,
    }).then((r) => bytesOf(r.blob));
  const model = buildQuoteModel({
    file,
    rows,
    currency: settings.currency,
    units: settings.units,
    flags,
    branding,
    user: session ? { email: session.email } : null,
    date: opts.date ?? new Date(),
    locale: navigator.language || 'en-US',
    includeAiRenders: opts.includeAiRenders,
  });
  const renders = [];
  for (const r of model.aiRenders) {
    const b = await bytesOf(await storage().blobs.getByUri(r.uri));
    if (b) renders.push({ label: `${TIME_LABEL[r.time]} render`, bytes: b });
  }
  const bytes = await buildQuotePdf(
    model,
    {
      hero: model.template === 'minimal' ? null : await image('demo'),
      lines: await image('lines'),
      logo: model.logo ? await bytesOf(await storage().blobs.getByUri(model.logo)) : null,
      renders,
    },
    await loadQuoteFonts(),
  );
  return { bytes, fileName: model.fileName, model };
}
